/**
 * ForceIncludeInPipelineButton — CEO 2026-09-08 emergency action.
 *
 * When a candidate has submitted their onboarding fiche but is NOT
 * showing up in `/embauche` (mysterious exclusion), this button clears
 * every exclusion flag (`set_aside`, `in_talent_pool`,
 * `removed_from_onboarding_at`) in one click so the candidate is
 * pushed back into the pipeline.
 *
 * Only rendered when :
 *   - user is admin, AND
 *   - the candidate has at least one exclusion flag set
 *     (otherwise the button would be a no-op).
 */
import { LifeBuoy, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { hasRole } from "../../lib/roles";
import { useAuth } from "../../lib/auth";

export default function ForceIncludeInPipelineButton({ candidate, onDone, dataTestid }) {
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);

    if (!user || !hasRole(user, "admin")) return null;
    if (!candidate) return null;

    const isExcluded =
        Boolean(candidate.set_aside)
        || Boolean(candidate.in_talent_pool)
        || Boolean(candidate.removed_from_onboarding_at);
    if (!isExcluded) return null;

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/hiring/pipeline/force-include/${candidate.id}`,
            );
            const flagsCleared = Object.entries(data.was_excluded || {})
                .filter(([, v]) => Boolean(v))
                .map(([k]) => k)
                .join(", ");
            toast.success(
                `Candidat réintégré au pipeline${flagsCleared ? ` — flags levés : ${flagsCleared}` : ""}`,
            );
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de ré-inclure");
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid={dataTestid || "force-include-in-pipeline"}
            title="Efface les flags set_aside / in_talent_pool / removed_from_onboarding_at pour que le candidat réapparaisse dans Embauche"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border-2 border-emerald-300 text-emerald-800 hover:bg-emerald-100 h-9 px-3.5 text-xs font-black uppercase tracking-widest transition disabled:opacity-60"
        >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LifeBuoy className="h-3.5 w-3.5" />}
            Ré-inclure au pipeline
        </button>
    );
}
